<template>
  <div id="app">
    <template v-if="loading" />
    <template v-else>
      <router-view />
    </template>
  </div>
</template>

<script>
import domainToLocationMap from './locations/domainToLocationMap'
import locationParamMap from './locations/locationParamMap'
import devSupporterDetails from '@/utilities/devSupporterDetails'
import { computed, reactive, ref } from 'vue'
import { useHead } from '@vueuse/head'
import { useRootStore } from '@/stores/root'
import { useDonationStore } from '@/stores/donation'

export default {
  name: 'ViruRoot',
  setup () {
    const loading = ref(true)
    const siteData = reactive({ title: 'Donate - Survival International', description: '' })
    useHead({
      title: computed(() => siteData.title),
      meta: [{ name: 'description', content: computed(() => siteData.description) }]
    })
    const rootStore = useRootStore()
    const donationStore = useDonationStore()
    return { siteData, rootStore, donationStore, loading }
  },
  beforeMount () {
    const currentDomain = window.location.hostname.replace(/local.|staging-/gi, '')
    this.rootStore.setCurrentDomain(currentDomain)

    const isEnDomain = (currentDomain === 'donate.survivalinternational.org')
    this.rootStore.setIsEnDomain(isEnDomain)

    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search))
    this.rootStore.setQueryParams(queryParams)
    this.donationStore.setUtms(queryParams)

    // ?hidefq=oneoff,monthly,quarterly,biAnnually
    const aliases = { one_off: 'oneOff', oneoff: 'oneOff', one: 'oneOff', reg: 'regular' }
    const rawParam = (queryParams.omitFrequency || queryParams.hidefq || '')
    const hiddenFrequencies = rawParam
      ? rawParam.split(',').map(f => aliases[f] || f).filter(Boolean)
      : []
    this.rootStore.setHiddenFrequencies(hiddenFrequencies)
    if (hiddenFrequencies.includes('oneOff')) {
      this.donationStore.setType('monthly')
    } else if (hiddenFrequencies.includes('regular')) {
      this.donationStore.setType('oneOff')
    }

    const currentLocationKey = this.locationFromParam(queryParams) || this.inferLocation(currentDomain)
    this.$l10n.changeLocation(currentLocationKey).then(() => {
      this.siteData.title = `${this.$t('meta.title')} - Survival International`
      if (this.appealExists(queryParams.appeal)) { this.rootStore.setAppeal(queryParams.appeal) }
      if (process.env.VUE_APP_DEV_MODE) { devSupporterDetails(this.donationStore, currentLocationKey) }
      this.loading = false
    })
  },
  methods: {
    inferLocation (domain) {
      return domainToLocationMap[domain] || 'us'
    },
    locationFromParam (queryParams) {
      return (('l' in queryParams) ? locationParamMap[queryParams.l] : false)
    },
    appealExists (appeal) {
      return (Object.keys(this.$tm(`appeals.${appeal}`)).length !== 0)
    }
  }
}
</script>

<style lang="scss">
@use 'assets/sass/layout';

#app {
  position: relative;
}
</style>
